import { useEffect, useState } from "react";
import { Plus, Trash2, FileText, Pencil, Save, X, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const EMPTY_PHASE = {
  order: 1, title: "", description: "", type: "task",
  requires_legal_review: false, requires_director_approval: false,
  responsible_user_id: "",
};

function ProductForm({ users, initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => initial || {
    name: "", slug: "", description: "", duration_months: 6,
    phases: [{ ...EMPTY_PHASE }],
  });
  const [insertOpen, setInsertOpen] = useState(false);
  const [insertPos, setInsertPos] = useState(1);

  const updatePhase = (idx, patch) => {
    setForm((prev) => {
      const phases = [...prev.phases];
      phases[idx] = { ...phases[idx], ...patch };
      return { ...prev, phases };
    });
  };

  const openInsertDialog = () => {
    setInsertPos(form.phases.length + 1);
    setInsertOpen(true);
  };

  const confirmInsert = () => {
    const max = form.phases.length + 1;
    const pos = Math.max(1, Math.min(parseInt(insertPos, 10) || 1, max));
    setForm((prev) => {
      const phases = [...prev.phases];
      phases.splice(pos - 1, 0, { ...EMPTY_PHASE, order: pos });
      // Reorder
      return { ...prev, phases: phases.map((p, i) => ({ ...p, order: i + 1 })) };
    });
    setInsertOpen(false);
    toast.success(`Fase inserida na posição ${pos}.`);
  };

  const removePhase = (idx) => setForm((prev) => ({
    ...prev,
    phases: prev.phases.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i + 1 })),
  }));

  const submit = () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error("Preencha nome e slug do produto.");
      return;
    }
    if (form.phases.length === 0 || form.phases.some(p => !p.title.trim())) {
      toast.error("Todas as fases precisam de título.");
      return;
    }
    // Normalize empty responsible_user_id
    const payload = {
      ...form,
      duration_months: Number(form.duration_months) || 6,
      phases: form.phases.map((p, i) => ({
        order: i + 1,
        title: p.title.trim(),
        description: p.description || "",
        type: p.type || "task",
        requires_legal_review: !!p.requires_legal_review,
        requires_director_approval: !!p.requires_director_approval,
        responsible_user_id: p.responsible_user_id || null,
      })),
    };
    onSave(payload);
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Nome do produto *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex.: Reforma Administrativa" data-testid="product-name-input" />
        </div>
        <div>
          <Label>Slug (identificador URL) *</Label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            placeholder="reforma-administrativa" data-testid="product-slug-input" />
        </div>
        <div>
          <Label>Duração (meses)</Label>
          <Input type="number" value={form.duration_months}
            onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
            min={1} max={120} data-testid="product-duration-input" />
        </div>
        <div className="sm:col-span-2">
          <Label>Descrição</Label>
          <Textarea rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-900">Fases do produto</div>
          <Button type="button" variant="outline" size="sm" onClick={openInsertDialog} data-testid="add-phase-button">
            <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar fase em posição…
          </Button>
        </div>
        <div className="space-y-3">
          {form.phases.map((ph, idx) => (
            <div key={idx} className="p-3 border border-slate-200 rounded-md bg-slate-50/50" data-testid={`phase-row-edit-${idx}`}>
              <div className="flex items-start gap-2">
                <span className="phase-number bg-midnight text-white mt-0.5 flex-shrink-0">{idx + 1}</span>
                <div className="flex-1 grid sm:grid-cols-2 gap-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Título da fase *</Label>
                    <Input value={ph.title} onChange={(e) => updatePhase(idx, { title: e.target.value })}
                      data-testid={`phase-title-${idx}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Descrição</Label>
                    <Textarea rows={2} value={ph.description}
                      onChange={(e) => updatePhase(idx, { description: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={ph.type} onValueChange={(v) => updatePhase(idx, { type: v })}>
                      <SelectTrigger className="h-9" data-testid={`phase-type-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="task">Tarefa</SelectItem>
                        <SelectItem value="meeting">Reunião</SelectItem>
                        <SelectItem value="document">Documento</SelectItem>
                        <SelectItem value="approval">Aprovação</SelectItem>
                        <SelectItem value="delivery">Entrega</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      <User className="w-3 h-3" /> Responsável pela fase (Diretora)
                    </Label>
                    <Select value={ph.responsible_user_id || "none"} onValueChange={(v) => updatePhase(idx, { responsible_user_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="h-9" data-testid={`phase-responsible-${idx}`}>
                        <SelectValue placeholder="Selecionar…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sem responsável —</SelectItem>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <Checkbox checked={ph.requires_legal_review}
                      onCheckedChange={(v) => updatePhase(idx, { requires_legal_review: !!v })}
                      data-testid={`phase-legal-${idx}`} />
                    Requer revisão jurídica
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <Checkbox checked={ph.requires_director_approval}
                      onCheckedChange={(v) => updatePhase(idx, { requires_director_approval: !!v })}
                      data-testid={`phase-director-${idx}`} />
                    Requer aprovação da Diretora
                  </label>
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={() => removePhase(idx)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={form.phases.length === 1}
                  data-testid={`remove-phase-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="product-cancel-button">
          <X className="w-4 h-4 mr-1" /> Cancelar
        </Button>
        <Button type="button" onClick={submit} disabled={saving}
          className="bg-midnight hover:bg-ink text-white" data-testid="product-save-button">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar produto"}
        </Button>
      </div>

      {/* Position dialog for inserting phase */}
      <Dialog open={insertOpen} onOpenChange={setInsertOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Inserir nova fase</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              O produto tem <strong>{form.phases.length}</strong> fase{form.phases.length === 1 ? "" : "s"}.
              Em qual posição deseja inserir a nova fase?
            </div>
            <div>
              <Label>Inserir na posição número</Label>
              <Input
                type="number"
                min={1}
                max={form.phases.length + 1}
                value={insertPos}
                onChange={(e) => setInsertPos(e.target.value)}
                placeholder={`1 a ${form.phases.length + 1}`}
                data-testid="insert-phase-position-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use <strong>{form.phases.length + 1}</strong> para inserir no final.
                As demais fases têm a numeração ajustada automaticamente.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsertOpen(false)}>Cancelar</Button>
            <Button onClick={confirmInsert} className="bg-midnight hover:bg-ink text-white" data-testid="insert-phase-confirm">
              Inserir fase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Products() {
  const [products, setProducts] = useState(null);
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/products").then(({ data }) => setProducts(data));
  };

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => setUsers([]));
  }, []);

  const saveNew = async (payload) => {
    setSaving(true);
    try {
      await api.post("/products", payload);
      toast.success("Produto criado com sucesso.");
      setOpenNew(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar produto.");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (payload) => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/products/${editing.id}`, payload);
      toast.success("Produto atualizado.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produto excluído.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir.");
    }
  };

  if (!products) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Produtos &amp; Fluxos</h1>
          <p className="text-sm text-slate-600 mt-1">
            Definições metodológicas dos serviços ofertados. Apenas a Diretora pode criar/editar.
          </p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="bg-midnight hover:bg-ink text-white" data-testid="new-product-button">
              <Plus className="w-4 h-4 mr-2" /> Novo produto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Criar novo produto / fluxo</DialogTitle>
            </DialogHeader>
            <ProductForm users={users} saving={saving} onSave={saveNew} onCancel={() => setOpenNew(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Editing dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar produto: {editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <ProductForm users={users} initial={editing} saving={saving} onSave={saveEdit} onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base">Catálogo de produtos ({products.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              Nenhum produto cadastrado. Clique em <strong>"Novo produto"</strong> para começar.
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {products.map((p) => (
                <AccordionItem key={p.id} value={p.id}>
                  <AccordionTrigger data-testid={`product-accordion-${p.id}`}>
                    <div className="flex items-center gap-3 text-left flex-1">
                      <FileText className="w-4 h-4 text-midnight" />
                      <div>
                        <div className="font-medium text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.phases.length} fases · {p.duration_months} meses</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex justify-end gap-2 mb-3">
                      <Button variant="outline" size="sm" onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deleteProduct(p.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`delete-product-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                      </Button>
                    </div>
                    <ol className="space-y-2">
                      {p.phases.map((ph, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md">
                          <span className="phase-number bg-midnight text-white flex-shrink-0">{i + 1}</span>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-slate-900">{ph.title}</div>
                            {ph.description && <div className="text-xs text-slate-600 mt-0.5">{ph.description}</div>}
                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                              <span>Tipo: {ph.type}</span>
                              {ph.requires_legal_review && <span>· Revisão jurídica</span>}
                              {ph.requires_director_approval && <span>· Aprovação da Diretora</span>}
                              {ph.responsible_user_name && (
                                <span className="inline-flex items-center gap-1 text-midnight">
                                  <User className="w-3 h-3" /> Responsável: <strong>{ph.responsible_user_name}</strong>
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
